import resolveFrontendModule from "@app/resolveModule/frontend";
import { useRouter } from "@core/Api/useRouter";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import EnterpriseApi from "@ext/enterprise/EnterpriseApi";
import t from "@ext/localization/locale/translate";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, LoadingButtonTemplate } from "@ui-kit/Button";
import { Dialog, DialogContent } from "@ui-kit/Dialog";
import { ContentDivider } from "@ui-kit/Divider";
import { Form, FormBody, FormField, FormStack } from "@ui-kit/Form";
import { Input } from "@ui-kit/Input";
import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Logo, TopContainerWrapper } from "../../../../components/HomePage/Welcome/Editor";
import FetchService from "../../../../ui-logic/ApiServices/FetchService";
import { useBreakpoint } from "../../../../ui-logic/hooks/useBreakpoint";
import { cn } from "../../../../ui-logic/utils/cn";
import { GesCloudSignInButtons } from "../../../enterpriseCommon/components/signIn/GesCloudSignInButtons";
import { getGesSignInUrl } from "./utils/getGesSignInUrl";

const formSchema = z.object({
	gesUrl: z.string({ message: t("must-be-not-empty") }),
});

interface SignInEnterpriseTauriFormProps {
	gesCloudUrl?: string;
}

export const SignInEnterpriseTauriForm = ({ gesCloudUrl: gesCloudUrlProp }: SignInEnterpriseTauriFormProps) => {
	const breakpoint = useBreakpoint();
	const [open, setOpen] = useState(true);
	// biome-ignore lint/correctness/useExhaustiveDependencies: it's ok
	const onOpenChangeHandler = useCallback(
		(value: boolean) => {
			setOpen(value);
			if (!value) ModalToOpenService.resetValue();
		},
		[setOpen],
	);

	const apiUrlCreator = ApiUrlCreatorService.value;
	const router = useRouter();

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		mode: "onChange",
		defaultValues: {
			gesUrl: PageDataContextService.value.conf.activeGesUrl ?? "",
		},
	});

	const abortController = useRef(new AbortController());

	const [isValidChecking, setIsValidChecking] = useState(false);

	const checkIfGesUrlIsValid = useCallback(
		async (gesUrl: string) => {
			setIsValidChecking(true);

			let errorMessage: string;
			if (URL.canParse(gesUrl)) {
				abortController.current.abort();
				abortController.current = new AbortController();

				const normalizedGesUrl = new URL(gesUrl).origin;
				const checkResult = await new EnterpriseApi(normalizedGesUrl).check();
				if (checkResult) errorMessage = "";
				else errorMessage = t("forms.sign-in-ges-tauri.errors.enterpriseServerIsNotAvailable");
			} else errorMessage = t("forms.sign-in-ges-tauri.errors.urlIsNotValid");

			setIsValidChecking(false);

			if (errorMessage) form.setError("gesUrl", { message: errorMessage });
			else form.clearErrors("gesUrl");

			return !errorMessage;
		},
		[form.setError, form.clearErrors],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: it's ok
	const handleSignIn = useCallback(async () => {
		const rawGesUrl = form.getValues("gesUrl")?.trim();
		if (!rawGesUrl) return;

		const isGesUrlValid = await checkIfGesUrlIsValid(rawGesUrl);
		if (!isGesUrlValid) return;

		const gesUrl = new URL(rawGesUrl).origin;

		await FetchService.fetch(apiUrlCreator.setGesUrl(gesUrl));
		const authUrl = getGesSignInUrl(gesUrl, false);
		await resolveFrontendModule("enterpriseLogin")(authUrl, apiUrlCreator, router);
	}, [form.getValues, apiUrlCreator, router, checkIfGesUrlIsValid]);

	const gesUrl = form.watch("gesUrl");
	const isSubmitDisabled = !gesUrl.trim();

	return (
		<Dialog onOpenChange={onOpenChangeHandler} open={open}>
			<DialogContent data-modal-root>
				<Form asChild {...form}>
					<form onSubmit={form.handleSubmit(handleSignIn)}>
						<div className="header flex flex-col justify-center items-center text-center p-6 pb-0 gap-3">
							<Logo isMobile={breakpoint === "sm"} />
							<TopContainerWrapper className={cn(breakpoint === "sm" && "mobile")}>
								<h1 className="text-2xl font-semibold sm:text-lg">{t("welcome.editor.title")}</h1>
								<div className="description text-base text-muted sm:text-sm font-normal">
									{t("enterprise-cloud-guest.description")}
								</div>
							</TopContainerWrapper>
						</div>
						<GesCloudSignInButtons
							className="px-4 py-5 lg:p-6"
							gesCloudUrl={gesCloudUrlProp && URL.canParse(gesCloudUrlProp) ? gesCloudUrlProp : undefined}
						/>
						<ContentDivider>
							<div className="text-sm text-center font-normal text-muted whitespace-nowrap">
								{t("or")}
							</div>
						</ContentDivider>
						<TopContainerWrapper className={cn(breakpoint === "sm" && "mobile")}>
							<div className="description text-center text-base text-muted sm:text-sm font-normal mt-3">
								{t("forms.sign-in-ges-tauri.name")}
							</div>
						</TopContainerWrapper>
						<FormBody>
							<FormStack>
								<FormField
									control={({ field }) => (
										<Input
											placeholder={t("forms.sign-in-ges-tauri.props.gesUrl.placeholder")}
											{...field}
										/>
									)}
									description={t("forms.sign-in-ges-tauri.props.gesUrl.description")}
									labelClassName="w-full"
									layout="vertical"
									name="gesUrl"
									required
									title={t("forms.sign-in-ges-tauri.props.gesUrl.name")}
								/>
								<div className="flex justify-center w-full">
									{isValidChecking ? (
										<LoadingButtonTemplate
											className="w-full"
											text={t("enterprise-guest.buttons.corporateLoginButton")}
											variant="outline"
										/>
									) : (
										<Button
											className="w-full"
											disabled={isSubmitDisabled}
											startIcon="building-2"
											type="submit"
											variant="outline"
										>
											{t("enterprise-guest.buttons.corporateLoginButton")}
										</Button>
									)}
								</div>
							</FormStack>
						</FormBody>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};
